import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight } from "lucide-react";

// These match the actual mailing_records DB columns
const DB_FIELDS = [
  { key: "document_number", label: "Document # / Seller ID" },
  { key: "county", label: "County" },
  { key: "filing_date", label: "Filing / Recorded Date" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "seller_full_name", label: "Seller / Grantor (full name)" },
  { key: "street_address", label: "Street Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip_code", label: "Zip Code" },
  { key: "property_address", label: "Property Address" },
  { key: "buyer", label: "Buyer / Grantee" },
  { key: "loan_amount", label: "Loan Amount / Consideration" },
  { key: "interest_rate", label: "Interest Rate" },
  { key: "monthly_payment", label: "Monthly Payment" },
  { key: "term_months", label: "Term (Months)" },
  { key: "balloon_amount", label: "Balloon Amount" },
  { key: "balloon_due_date", label: "Balloon Due Date" },
  { key: "confidence_score", label: "Confidence Score" },
  { key: "document_type", label: "Document Type" },
  { key: "origin_state", label: "Origin State" },
  { key: "business_name", label: "Business Name" },
  { key: "phone_number", label: "Phone Number" },
  { key: "md_results", label: "Melissa Results (MD Results)" },
] as const;

export type ColumnMapping = Record<string, string>; // db_field -> excel_column

// Auto-detect patterns — MD fields map directly to the DB columns
const AUTO_DETECT_PATTERNS: Record<string, string[]> = {
  document_number: ["documentnumber", "documentno", "docnumber", "docno", "sellerid", "recordid", "recordnumber"],
  county: ["county"],
  filing_date: ["filingdate", "recordeddate", "recordingdate", "daterecorded", "fileddate"],
  first_name: ["firstname", "first"],
  last_name: ["lastname", "last"],
  seller_full_name: ["sellergrantor", "grantor", "sellername", "fullname"],
  street_address: ["mdaddress1", "mdaddress", "addressline1", "streetaddress", "address1", "street", "address"],
  city: ["mdcity", "city"],
  state: ["mdstate"],
  zip_code: ["mdpostalcode", "mdzip", "postalcode", "zipcode", "zip"],
  property_address: ["propertyaddress"],
  buyer: ["buyergrantee", "grantee", "buyer", "buyername"],
  loan_amount: ["loanamount", "consideration", "amount"],
  interest_rate: ["interestrate", "rate"],
  monthly_payment: ["monthlypayment", "payment"],
  term_months: ["termmonths", "term"],
  balloon_amount: ["balloonamount", "balloon"],
  balloon_due_date: ["balloonduedate"],
  confidence_score: ["confidencescore"],
  document_type: ["documenttype", "doctype", "instrument", "instrumenttype"],
  origin_state: ["originstate", "stateorigin"],
  business_name: ["businessname", "business", "companyname", "company", "entityname"],
  phone_number: ["phonenumber", "phone", "telephone", "tel", "mdphone", "phonenumber1"],
  md_results: ["mdresults", "melissaresults"],
};

function autoDetect(excelHeaders: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const usedHeaders = new Set<string>();

  for (const field of DB_FIELDS) {
    const patterns = AUTO_DETECT_PATTERNS[field.key] || [];
    for (const pattern of patterns) {
      let matched = false;
      for (const header of excelHeaders) {
        if (usedHeaders.has(header)) continue;
        const norm = header.toLowerCase().replace(/[^a-z0-9]/g, "");
        if (norm === pattern || norm.includes(pattern)) {
          mapping[field.key] = header;
          usedHeaders.add(header);
          matched = true;
          break;
        }
      }
      if (matched) break;
    }
  }
  return mapping;
}

interface MelissaColumnMapperProps {
  excelHeaders: string[];
  sampleRows: Record<string, any>[];
  onConfirm: (mapping: ColumnMapping) => void;
  onCancel: () => void;
}

export function MelissaColumnMapper({ excelHeaders, sampleRows, onConfirm, onCancel }: MelissaColumnMapperProps) {
  const [mapping, setMapping] = useState<ColumnMapping>(() => autoDetect(excelHeaders));

  const mappedCount = Object.values(mapping).filter(Boolean).length;

  const getSample = (excelCol: string) => {
    if (!excelCol) return "";
    for (const row of sampleRows) {
      const val = row[excelCol];
      if (val != null && val !== "") return String(val).slice(0, 40);
    }
    return "";
  };

  const handleChange = (dbField: string, excelCol: string) => {
    setMapping(prev => {
      const next = { ...prev };
      if (excelCol === "__none__") {
        delete next[dbField];
      } else {
        next[dbField] = excelCol;
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Column Mapping — {mappedCount} of {DB_FIELDS.length} fields mapped</p>
          <p className="text-xs text-muted-foreground">
            {excelHeaders.length} columns found in file. Review and adjust mappings below.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Back</Button>
          <Button size="sm" onClick={() => onConfirm(mapping)}>
            <ArrowRight className="h-4 w-4 mr-1" />
            Continue to Preview
          </Button>
        </div>
      </div>

      <div className="border rounded-md overflow-auto max-h-[500px]">
        <Table>
          <TableHeader>
            <TableRow className="text-xs">
              <TableHead className="w-[200px]">Database Field</TableHead>
              <TableHead className="w-[220px]">Excel Column</TableHead>
              <TableHead>Sample Value</TableHead>
              <TableHead className="w-[60px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {DB_FIELDS.map(field => {
              const mapped = mapping[field.key];
              return (
                <TableRow key={field.key} className="text-xs">
                  <TableCell className="font-medium whitespace-nowrap">
                    {field.label}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={mapped || "__none__"}
                      onValueChange={(v) => handleChange(field.key, v)}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="— Not mapped —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Not mapped —</SelectItem>
                        {excelHeaders.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono truncate max-w-[200px]">
                    {mapped ? getSample(mapped) : "—"}
                  </TableCell>
                  <TableCell>
                    {mapped ? (
                      <Badge variant="default" className="text-[10px]"><Check className="h-3 w-3" /></Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">—</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        Tip: MD Address, MD City, MD State, MD Zip will auto-map to Street Address, City, State, Zip Code.
        If "Document #" is not in your file, an auto-generated ID will be created.
      </p>
    </div>
  );
}
